/**
 * Labstep
 *
 * @module services/ketcher
 * @desc Ketcher Service. Replacement for         // Generate image using Ketcher
        const imageBlob = await this.ketcher!.generateImage(dataForImage, {
          outputFormat: outputFormat as any, // Type assertion for Ketcher's OutputFormatType
          bondThickness: 2,
        });nJS using Ketcher/Indigo.
 */

import { ChemicalMimeType, type Ketcher } from 'ketcher-core';
import type { Indigo } from 'ketcher-core/dist/application/indigo';
import { trimSvgBlob } from 'labstep-web/core/ChemDraw/utils';

export interface AvailableFormat {
  smiles: ChemicalMimeType.DaylightSmiles;
  mol: ChemicalMimeType.Mol;
  ket: ChemicalMimeType.KET;
  inchi: ChemicalMimeType.InChI;
}

export interface ElementalAnalysis {
  formula: string;
  mass: number;
}

/**
 * Ketcher Service. Replacement for MarvinJS using Ketcher/Indigo.
 */
export class KetcherService {
  private ketcher?: Ketcher;
  private indigo?: Indigo;

  /**
   * Initialize the service with Ketcher instance
   * @param ketcher Ketcher instance
   */
  public initialize(ketcher: Ketcher): void {
    this.ketcher = ketcher;
    this.indigo = ketcher.indigo;
  }

  /**
   * Check if the service is ready to use
   */
  public isReady(): boolean {
    return !!(this.ketcher && this.indigo);
  }

  /**
   * Get SMILES from structure data
   * @param data Structure data
   * @returns Structure in SMILES format
   */
  public async getSmiles(data: string): Promise<string> {
    return await this.getStructure(data, 'smiles', null);
  }

  /**
   * Convert structure between different formats
   * @param structure Structure to convert
   * @param outputFormat Format to convert to
   * @param inputFormat Format to convert from (optional, will be auto-detected)
   * @returns Converted structure
   */
  public async getStructure(
    structure: string,
    outputFormat: 'png' | 'svg' | keyof AvailableFormat,
    inputFormat: keyof AvailableFormat | null,
  ): Promise<string> {
    if (!this.isReady()) {
      throw new Error(
        'KetcherService not initialized. Call initialize() first.',
      );
    }

    try {
      // Handle image formats using Ketcher's generateImage
      if (outputFormat === 'svg' || outputFormat === 'png') {
        // For image generation, we need to use the structure as is
        // Ketcher's generateImage expects the data in a compatible format
        let dataForImage = structure;

        // If we have a specific input format, convert to KET first for better image generation
        if (inputFormat && inputFormat !== 'ket') {
          const convertedData = await this.indigo!.convert(
            structure,
            {
              inputFormat: this.getChemicalMimeType(inputFormat),
              outputFormat: ChemicalMimeType.KET,
            },
          );
          dataForImage = convertedData.struct;
        }

        // Generate image using Ketcher
        const imageBlob = await this.ketcher!.generateImage(
          dataForImage,
          {
            outputFormat: outputFormat as 'svg' | 'png',
            bondThickness: 2,
          },
        );

        if (outputFormat === 'svg') {
          return await trimSvgBlob(imageBlob);
        } else {
          // For PNG/JPEG, we need to convert blob to base64 data URL
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(imageBlob);
          });
        }
      }

      // Handle text-based format conversions using Indigo
      const inputMimeType = inputFormat
        ? this.getChemicalMimeType(inputFormat)
        : undefined;
      const outputMimeType = this.getChemicalMimeType(
        outputFormat as keyof AvailableFormat,
      );

      const result = await this.indigo!.convert(structure, {
        inputFormat: inputMimeType,
        outputFormat: outputMimeType,
      });

      return result.struct;
    } catch (error) {
      console.error('Structure conversion failed:', error);
      throw new Error(`Failed to convert structure: ${error}`);
    }
  }

  /**
   * Get chemical properties (formula and mass) from structure
   * @param source Structure data
   * @returns Chemical properties
   */
  public async getChemicalProperties(
    source: string,
  ): Promise<Pick<ElementalAnalysis, 'formula' | 'mass'>[]> {
    if (!this.isReady()) {
      throw new Error(
        'KetcherService not initialized. Call initialize() first.',
      );
    }

    try {
      // Convert to MOL format first if needed, then use Indigo to get properties

      // Try to detect if it's already in a format Indigo can work with
      try {
        // Convert to KET first, then get properties
        const ketResult = await this.indigo!.convert(source, {
          outputFormat: ChemicalMimeType.KET,
        });

        // For now, we'll need to implement molecular formula and mass calculation
        // This is a simplified version - you might want to use a more robust approach
        const molResult = await this.indigo!.convert(
          ketResult.struct,
          {
            inputFormat: ChemicalMimeType.KET,
            outputFormat: ChemicalMimeType.Mol,
          },
        );

        // Parse MOL file to extract formula and calculate mass
        // This is a basic implementation - you might want to enhance it
        const formula = this.extractFormulaFromMol(molResult.struct);
        const mass = this.calculateMolecularMass(formula);

        return [{ formula, mass }];
      } catch (error) {
        console.error(
          'Error calculating chemical properties:',
          error,
        );
        throw error;
      }
    } catch (error) {
      console.error('Chemical properties calculation failed:', error);
      throw new Error(`Failed to get chemical properties: ${error}`);
    }
  }

  /**
   * Map format names to ChemicalMimeType
   */
  private getChemicalMimeType(
    format: keyof AvailableFormat,
  ): ChemicalMimeType {
    const formatMap: Record<keyof AvailableFormat, ChemicalMimeType> =
      {
        smiles: ChemicalMimeType.DaylightSmiles,
        mol: ChemicalMimeType.Mol,
        ket: ChemicalMimeType.KET,
        inchi: ChemicalMimeType.InChI,
      };

    return formatMap[format];
  }

  /**
   * Extract molecular formula from MOL file (basic implementation)
   */
  private extractFormulaFromMol(molData: string): string {
    // This is a simplified implementation
    // In a real scenario, you might want to use Indigo's built-in methods
    const lines = molData.split('\n');
    const atomCounts: Record<string, number> = {};

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.length > 30) {
        const parts = trimmed.split(/\s+/);
        if (parts.length >= 4) {
          const element = parts[3];
          if (element && element.match(/^[A-Z][a-z]?$/)) {
            atomCounts[element] = (atomCounts[element] || 0) + 1;
          }
        }
      }
    }

    // Build formula string
    let formula = '';
    const elementOrder = ['C', 'H', 'N', 'O', 'P', 'S'];

    elementOrder.forEach((element) => {
      if (atomCounts[element]) {
        formula +=
          element +
          (atomCounts[element] > 1 ? atomCounts[element] : '');
        delete atomCounts[element];
      }
    });

    // Add remaining elements alphabetically
    Object.keys(atomCounts)
      .sort()
      .forEach((element) => {
        formula +=
          element +
          (atomCounts[element] > 1 ? atomCounts[element] : '');
      });

    return formula || 'Unknown';
  }

  /**
   * Calculate molecular mass from formula (basic implementation)
   */
  private calculateMolecularMass(formula: string): number {
    // Basic atomic masses
    const atomicMasses: Record<string, number> = {
      H: 1.008,
      C: 12.011,
      N: 14.007,
      O: 15.999,
      P: 30.974,
      S: 32.065,
      Cl: 35.453,
      Br: 79.904,
      I: 126.904,
      F: 18.998,
      Na: 22.99,
      K: 39.098,
      Ca: 40.078,
      Mg: 24.305,
      Fe: 55.845,
      Zn: 65.38,
    };

    let mass = 0;
    const matches = formula.match(/([A-Z][a-z]?)(\d*)/g);

    if (matches) {
      matches.forEach((match) => {
        const elementMatch = match.match(/([A-Z][a-z]?)(\d*)/);
        if (elementMatch) {
          const element = elementMatch[1];
          const count = parseInt(elementMatch[2]) || 1;
          mass += (atomicMasses[element] || 0) * count;
        }
      });
    }

    return mass;
  }
}

// Create a singleton instance
const ketcherService = new KetcherService();

export default ketcherService;
